import React from "react";
import styled from "styled-components";
import {
  COURSES_GROUP_KINDS,
  FilterInfo,
  isSchoolUser,
  Lesson,
  LessonOrUnitGroup,
  LessonOrUnitGroupKind,
  Unit,
} from "../common/api";
import {
  FeaturedUnitItem,
  FeaturedUnitLoggingContext,
} from "../components/FeaturedUnitItem";
import { RecentlyActiveUnitItem } from "../components/RecentlyActiveUnitItem";
import { LessonItem, LessonItemLoggingContext } from "../components/LessonItem";
import { LeftCornerDecoration } from "../components/LessonItemBody";
import { UnitItem, UnitItemLoggingContext } from "../components/UnitItem";
import { StudentAssignmentGroupCard } from "../components/StudentAssignmentGroupCard";
import { UnitFilterControls, useUnitFilters } from "../components/UnitFilters";
import { UnitSearchResults, useUnitSearch } from "../components/UnitSearch";
import { ApiContext } from "../common/context/ApiContext";
import { TabEnum, TabsContext } from "../common/context/TabsContext";
import { useLoadingIndicator } from "../common/context/LoadingIndicatorContext";
import { useGridColumnCount, useLiveData } from "../common/utils";
import { Account, AccountContext } from "../common/context/AccountContext";
import { Redirect } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { default as UUID } from "uuid";
import { SPACE_SM, SPACE_MD, SPACE_LG, SPACE_XL } from "../common/spacing";
import {
  ASSIGNMENT_CARD_MIN_WIDTH,
  GENERIC_ITEM_BORDER_RADIUS,
  LESSON_CARD_MIN_WIDTH,
} from "../common/constants";
import {
  COLOR_SURFACE_CONTAINER_STRONG,
  COLOR_TEXT_PRIMARY,
} from "../common/colors";
import {
  EmptyResults,
  SECTION_SPACING,
  Section,
  SectionHeading,
  UnitTilesRow,
} from "../common/CommonStyles";

const WEEKLY_SPECIALS_TILE_GAP = SPACE_SM;
const SHOW_MORE_FONT_SIZE = 14;
const SHOW_MORE_BUTTON_WIDTH = 150;

export const HomePageRedirector: React.FC = () => {
  const [destination, setDestination] = React.useState<string | null>(null);
  const { accountLiveData } = React.useContext(AccountContext);

  useLiveData(
    React.useCallback((account: Account) => {
      if (account.hasUserAndMaybeProfile()) {
        setDestination(TabEnum.Home);
      }
    }, []),
    accountLiveData
  );
  if (destination) {
    return <Redirect to={destination} />;
  }
  return <></>;
};

const SectionRow = styled.div<{ $gap: number }>`
  display: flex;
  flex-wrap: wrap;
  gap: ${(p) => p.$gap}px;
  padding: ${SECTION_SPACING}px 0;
`;

const AssignmentCardsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(min(100%, ${ASSIGNMENT_CARD_MIN_WIDTH}px), 1fr)
  );
  align-items: start;
  row-gap: ${SPACE_LG}px;
  column-gap: ${SPACE_XL}px;
  padding: ${SECTION_SPACING}px 0;

  & > * {
    min-width: 0;
  }
`;

const LessonsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(${LESSON_CARD_MIN_WIDTH}px, 1fr)
  );
  row-gap: ${SPACE_LG}px;
  column-gap: ${SPACE_XL}px;
  padding: ${SECTION_SPACING}px 0;

  & > * {
    min-width: 0;
  }
`;

const ScrollingSectionRow = styled.div`
  display: flex;
  gap: ${SPACE_MD}px;
  padding: ${SECTION_SPACING}px 0;
  overflow-x: auto;

  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }

  & > * {
    flex: 0 0 auto;
  }
`;

const ShowMoreButton = styled.button`
  display: block;
  width: ${SHOW_MORE_BUTTON_WIDTH}px;
  margin: ${SPACE_MD}px auto;
  padding: ${SPACE_SM}px ${SPACE_LG}px;
  border: none;
  border-radius: ${GENERIC_ITEM_BORDER_RADIUS}px;
  background-color: ${COLOR_SURFACE_CONTAINER_STRONG};
  color: ${COLOR_TEXT_PRIMARY};
  font-size: ${SHOW_MORE_FONT_SIZE}px;
  font-weight: 600;
`;

type HomeState = {
  groups: LessonOrUnitGroup[] | null;
  filterInfo?: FilterInfo;
};

const SUGGESTED_LESSONS_STACKED_LIMIT = 3;

export const HomePage: React.FC = () => {
  const [state, setState] = React.useState<HomeState>({ groups: null });
  const { setCurrentTab } = React.useContext(TabsContext);
  const { startLoading, stopLoading } = useLoadingIndicator(true);
  const { accountLiveData } = React.useContext(AccountContext);
  const { getHomeMetadata } = React.useContext(ApiContext);
  const { t } = useTranslation();
  const [account, setAccount] = React.useState<Account>();
  const homeFetchedForProfileStateVersion = React.useRef<number | null>(null);
  const fetchJobForProfileStateVersion = React.useRef<number | null>(null);
  const currentFetchJobUuid = React.useRef<string | null>(null);
  const filters = useUnitFilters(TabEnum.Home, state.filterInfo);
  const search = useUnitSearch(state.groups !== null);
  const [suggestedSection, setSuggestedSection] =
    React.useState<HTMLDivElement | null>(null);
  const [isSuggestedExpanded, setIsSuggestedExpanded] = React.useState(false);
  const suggestedColumnCount = useGridColumnCount(
    suggestedSection,
    LESSON_CARD_MIN_WIDTH,
    SPACE_XL
  );
  const suggestedLimit =
    suggestedColumnCount >= 2
      ? suggestedColumnCount
      : SUGGESTED_LESSONS_STACKED_LIMIT;

  React.useEffect(() => {
    setCurrentTab(TabEnum.Home);
  }, [setCurrentTab]);

  const maybeRefetchHome = React.useCallback(
    async (account: Account) => {
      if (!account.hasMaybeProfileState()) {
        return;
      }
      setAccount(account);
      if (
        account.profileStateCurrentVersion ===
          homeFetchedForProfileStateVersion.current ||
        account.profileStateCurrentVersion ===
          fetchJobForProfileStateVersion.current
      ) {
        return;
      }
      startLoading();
      const fetchJobUuid = (currentFetchJobUuid.current = UUID.v4());
      fetchJobForProfileStateVersion.current =
        account.profileStateCurrentVersion;
      const homeMetadata = await getHomeMetadata(
        account.profile?.id || null,
        account.profileState?.lessons_in_progress || {}
      );
      if (currentFetchJobUuid.current !== fetchJobUuid) {
        return;
      }
      setState({
        groups: homeMetadata.groups,
        filterInfo: homeMetadata.filter_info,
      });
      homeFetchedForProfileStateVersion.current =
        fetchJobForProfileStateVersion.current;
      stopLoading();
    },
    [getHomeMetadata, startLoading, stopLoading]
  );

  useLiveData(
    React.useCallback(
      (account: Account) => {
        maybeRefetchHome(account);
      },
      [maybeRefetchHome]
    ),
    accountLiveData
  );

  if (!state.groups || !account) {
    return <></>;
  }

  const isVisibleToUser = (group: LessonOrUnitGroup) =>
    group.group !== LessonOrUnitGroupKind.StudentAssignedLessons ||
    isSchoolUser(account.user);

  const lessonsGrid = (lessons: Lesson[]) =>
    lessons.length > 0 ? (
      <LessonsRow>
        {lessons.map((lesson) => (
          <LessonItem
            key={`${lesson.id}-${lesson.custom_flashpack_id}`}
            lesson={lesson}
            unitId={null}
            account={account}
            leftCornerDecoration={LeftCornerDecoration.STANDARD}
            loggingContext={LessonItemLoggingContext.Suggested}
          />
        ))}
      </LessonsRow>
    ) : null;

  const unitsRow = (
    units: Unit[],
    gap: number,
    renderUnit: (unit: Unit) => React.ReactNode
  ) =>
    units.length > 0 ? (
      <SectionRow $gap={gap}>{units.map(renderUnit)}</SectionRow>
    ) : null;

  const toggleSuggested = () => {
    if (isSuggestedExpanded) {
      suggestedSection?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    setIsSuggestedExpanded(!isSuggestedExpanded);
  };

  const sectionBody = (group: LessonOrUnitGroup): React.ReactNode => {
    switch (group.group) {
      case LessonOrUnitGroupKind.SuggestedLessons: {
        const lessons = group.lessons ?? [];
        return lessonsGrid(
          isSuggestedExpanded ? lessons : lessons.slice(0, suggestedLimit)
        );
      }
      case LessonOrUnitGroupKind.WeeklySpecials:
        return unitsRow(group.units ?? [], WEEKLY_SPECIALS_TILE_GAP, (unit) => (
          <FeaturedUnitItem
            key={unit.id}
            unit={unit}
            loggingContext={FeaturedUnitLoggingContext.WeeklySpecial}
          />
        ));
      case LessonOrUnitGroupKind.RecentActivity: {
        const units = group.units ?? [];
        return units.length > 0 ? (
          <ScrollingSectionRow>
            {units.map((unit) => (
              <RecentlyActiveUnitItem
                key={unit.id}
                unit={unit}
                completedLessonCount={
                  account.profileState?.units_state[unit.id]?.completed_count ||
                  0
                }
              />
            ))}
          </ScrollingSectionRow>
        ) : null;
      }
      default:
        return null;
    }
  };

  const coursesGroup = state.groups.find((group) =>
    COURSES_GROUP_KINDS.has(group.group)
  );
  const discoverGroups = state.groups.filter(
    (group) => !COURSES_GROUP_KINDS.has(group.group) && isVisibleToUser(group)
  );
  const assignmentGroups = discoverGroups.filter(
    (group) =>
      group.group === LessonOrUnitGroupKind.StudentAssignedLessons &&
      (group.lessons ?? []).length > 0
  );
  const nonAssignmentGroups = discoverGroups.filter(
    (group) => group.group !== LessonOrUnitGroupKind.StudentAssignedLessons
  );
  const filteredCourses = (coursesGroup?.units ?? []).filter(filters.matches);

  if (search.committedQuery !== null) {
    return (
      <>
        <UnitFilterControls filters={filters} search={search} />
        <UnitSearchResults search={search} />
      </>
    );
  }

  return (
    <>
      <UnitFilterControls filters={filters} search={search} />

      {!filters.isFiltering && assignmentGroups.length > 0 && (
        <Section>
          <AssignmentCardsRow>
            {assignmentGroups.map((group) => (
              <StudentAssignmentGroupCard
                key={`${group.group}-${group.assignment_info?.teacher_id}`}
                group={group}
                account={account}
              />
            ))}
          </AssignmentCardsRow>
        </Section>
      )}

      {!filters.isFiltering &&
        nonAssignmentGroups.map((group) => {
          const body = sectionBody(group);
          if (!body) {
            return null;
          }
          const isSuggested =
            group.group === LessonOrUnitGroupKind.SuggestedLessons;
          return (
            <Section
              key={group.group}
              ref={isSuggested ? setSuggestedSection : undefined}
            >
              <SectionHeading>{group.title}</SectionHeading>
              {body}
              {isSuggested && (group.lessons ?? []).length > suggestedLimit && (
                <ShowMoreButton onClick={toggleSuggested}>
                  {t(isSuggestedExpanded ? "home_show_hide" : "home_show_more")}
                </ShowMoreButton>
              )}
            </Section>
          );
        })}

      {coursesGroup && (filteredCourses.length > 0 || filters.isFiltering) && (
        <Section key={coursesGroup.group}>
          <SectionHeading>
            {filters.sectionTitle(coursesGroup.title)}
          </SectionHeading>
          {filteredCourses.length > 0 ? (
            <UnitTilesRow>
              {filteredCourses.map((unit) => (
                <UnitItem
                  key={unit.id}
                  unit={unit}
                  loggingContext={UnitItemLoggingContext.Unit}
                />
              ))}
            </UnitTilesRow>
          ) : (
            <EmptyResults message={t("filter_no_results")} />
          )}
        </Section>
      )}
    </>
  );
};
